import { getSession, signOut } from "@client/lib/auth-client";
import { useEffect, useState } from "react";

export interface User {
	id: string;
	name: string;
	email: string;
	emailVerified: boolean;
	image?: string | null;
	waitlist: boolean;
	createdAt: Date;
	updatedAt: Date;
}

interface UseAuthReturn {
	isAuthenticated: boolean;
	user: User | null;
	isLoading: boolean;
	logout: () => Promise<void>;
}

export function useAuth(): UseAuthReturn {
	const [user, setUser] = useState<User | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isLoggingOut, setIsLoggingOut] = useState(false);

	useEffect(() => {
		const checkAuth = async () => {
			try {
				const sessionData = await getSession();

				// Handle different possible session data structures
				let actualSession = null;
				if (sessionData && typeof sessionData === "object") {
					// Check if it's { data: { user, session } } format
					if ("data" in sessionData && sessionData.data) {
						actualSession = sessionData.data;
					}
					// Check if it's direct { user, session } format
					else if ("user" in sessionData) {
						actualSession = sessionData;
					}
				}

				setUser(actualSession?.user as User | null);
			} catch (error) {
				console.error("🚨 useAuth: Authentication check error:", error);
				setUser(null);
			} finally {
				setIsLoading(false);
			}
		};

		checkAuth();

		// Listen for auth state changes (if supported by better-auth)
		const handleStorageChange = () => {
			console.log("🔍 useAuth: Storage change detected, re-checking auth");
			checkAuth();
		};

		// Listen for URL changes (for callback handling)
		const handleLocationChange = () => {
			console.log(
				"🔍 useAuth: Location change detected:",
				window.location.href,
			);
			// Small delay to allow callback processing
			setTimeout(() => {
				console.log("🔍 useAuth: Re-checking auth after location change");
				checkAuth();
			}, 1000);
		};

		window.addEventListener("storage", handleStorageChange);
		window.addEventListener("popstate", handleLocationChange);

		// Check if this is a callback URL
		if (window.location.pathname.includes("/api/auth/callback")) {
			console.log(
				"🔍 useAuth: Detected auth callback URL, will re-check auth in 2 seconds",
			);
			setTimeout(() => {
				checkAuth();
			}, 2000);
		}

		return () => {
			window.removeEventListener("storage", handleStorageChange);
			window.removeEventListener("popstate", handleLocationChange);
		};
	}, []);

	const logout = async () => {
		if (isLoggingOut) return;

		setIsLoggingOut(true);
		try {
			console.log("🔍 useAuth: Starting logout");
			await signOut();
			setUser(null);
			// Force page reload to reset app state
			window.location.reload();
		} catch (error) {
			console.error("🚨 useAuth: Logout error:", error);
			setIsLoggingOut(false);
		}
	};

	return {
		isAuthenticated: !!user,
		user,
		isLoading,
		logout,
	};
}
